import React, { useState } from 'react';
import axiosInstance from '../Core/RestHelper';
import { InviteLinkResponse } from '../Model/Teams';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvited: () => void;
  seatsUsed: number;
  seatsAllowed: string;        // "unlimited" or a numeric string
  pendingInvitesCount: number; // counted against the seat limit same as the backend gate
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(5,5,8,.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};

const modalStyle: React.CSSProperties = {
  background: '#111116', border: '1px solid #26262f', borderRadius: '12px',
  padding: '22px', width: '380px', maxWidth: '90vw', fontFamily: "'Inter',sans-serif",
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 11px', borderRadius: '7px',
  background: '#17171e', border: '1px solid #32323d', color: '#eeeef5',
  fontSize: '13px', fontFamily: "'Inter',sans-serif", outline: 'none', boxSizing: 'border-box' as const,
};

const modalBtn = (variant: 'primary' | 'secondary' = 'secondary'): React.CSSProperties => ({
  padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 500,
  cursor: 'pointer', border: 'none', fontFamily: "'Inter',sans-serif",
  ...(variant === 'primary'
    ? { background: '#6366f1', color: '#fff' }
    : { background: '#17171e', color: '#eeeef5', border: '1px solid #32323d' }),
});

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  onInvited,
  seatsUsed,
  seatsAllowed,
  pendingInvitesCount,
}) => {
  const [email, setEmail] = useState('');
  const [canAddConnections, setCanAddConnections] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const seatsAllowedLabel = seatsAllowed === 'unlimited' ? 'Unlimited' : seatsAllowed;
  const seatsAllowedNum = seatsAllowed === 'unlimited' ? Infinity : parseInt(seatsAllowed, 10) || 1;
  const seatsCombinedUsed = seatsUsed + pendingInvitesCount;
  const seatsFull = seatsCombinedUsed >= seatsAllowedNum;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await axiosInstance.post<InviteLinkResponse>('/teams/invite', {
        email: email.trim() || undefined,
        can_add_connections: canAddConnections,
      });
      setInviteLink(res.data.invite_link);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not create invite. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleClose = () => {
    const hadInvite = !!inviteLink;
    setEmail('');
    setCanAddConnections(true);
    setError(null);
    setInviteLink(null);
    onClose();
    if (hadInvite) onInvited();
  };

  return (
    <div style={overlay} onClick={handleClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>

        {/* Seat usage — always visible, even after an invite is created */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 10px', background: '#17171e', border: '1px solid #26262f',
          borderRadius: '7px', marginBottom: '14px',
        }}>
          <span style={{ fontSize: '11.5px', color: '#8e8ea8' }}>Seats</span>
          <span style={{ fontSize: '12px', fontWeight: 600, color: seatsFull ? '#f87171' : '#eeeef5' }}>
            {seatsCombinedUsed} of {seatsAllowedLabel} used
          </span>
        </div>

        {!inviteLink ? (
          seatsFull ? (
            <>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5', marginBottom: '4px' }}>
                Seat limit reached
              </div>
              <p style={{ fontSize: '12.5px', color: '#8e8ea8', marginBottom: '20px' }}>
                You've used all available seats on your plan. Upgrade your plan, or revoke a pending invite / remove a member to free one up.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button style={modalBtn('secondary')} onClick={handleClose}>Close</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5', marginBottom: '4px' }}>
                Invite a team member
              </div>
              <p style={{ fontSize: '12.5px', color: '#8e8ea8', marginBottom: '12px' }}>
                They'll sign in with GitHub to accept.
              </p>

              <div style={{
                fontSize: '11.5px', color: '#8e8ea8', lineHeight: 1.5,
                background: '#17171e', border: '1px solid #26262f', borderRadius: '7px',
                padding: '10px 12px', marginBottom: '18px',
              }}>
                <strong style={{ color: '#eeeef5' }}>Before you invite:</strong> this person needs to already
                be part of your GitHub organization (or a collaborator) with access to at least one
                repository covered by your installation. We check this automatically when they accept —
                if they don't have access yet, the invite will be rejected and they'll need to be added
                on GitHub first.
              </div>

              <label style={{ fontSize: '12px', color: '#8e8ea8', display: 'block', marginBottom: '6px' }}>
                Email <span style={{ color: '#52525f' }}>(optional — leave blank to share the link manually)</span>
              </label>
              <input
                style={{ ...inputStyle, marginBottom: '16px' }}
                type="email"
                placeholder="teammate@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#eeeef5', marginBottom: '20px', cursor: 'pointer' }}>
                <input type="checkbox" checked={canAddConnections} onChange={e => setCanAddConnections(e.target.checked)} />
                Allow adding new AWS/Azure connections
              </label>

              {error && <div style={{ fontSize: '12px', color: '#f87171', marginBottom: '12px' }}>{error}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button style={modalBtn('secondary')} onClick={handleClose}>Cancel</button>
                <button style={{ ...modalBtn('primary'), opacity: isSubmitting ? 0.6 : 1 }} onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Send Invite'}
                </button>
              </div>
            </>
          )
        ) : (
          <>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5', marginBottom: '4px' }}>
              Invite created
            </div>
            <p style={{ fontSize: '12.5px', color: '#8e8ea8', marginBottom: '14px' }}>
              {email ? `We've emailed this to ${email}. You` : 'You'} can also share the link directly:
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
              <input style={{ ...inputStyle, flex: 1, color: '#8e8ea8' }} value={inviteLink} readOnly />
              <button style={modalBtn('secondary')} onClick={handleCopy}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button style={modalBtn('primary')} onClick={handleClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};